import { type BenefitConfig, BenefitType, CalculationMethod, type Category } from '../types';

export const formatCurrency = (val: number, currency: string = 'R') => {
  return `${currency} ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatWageroll = (val: number, currency: string = 'R') => {
  return `${currency} ${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const formatPct = (val: number) => {
  return (
    (val * 100).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) +
    '%'
  );
};

/**
 * Derives the displayed Rate and Exposure strings for a given benefit.
 * Used by both QuoteEngine (Editor) and QuoteOutput (PDF) to ensure consistency.
 */
export const getBenefitCalculationDisplay = (
  cat: Category,
  ben: BenefitConfig,
  currencySymbol: string,
) => {
  const grossRate = ben.grossRate || 0;
  let rateDisplay = '';
  let exposureDisplay = '';

  if (ben.type === BenefitType.Medical) {
    // Medical Logic: Fixed (Rand/Life) vs Stated (% Wageroll)
    const isFixedBasis =
      ben.medicalBasis === 'Fixed' || (!ben.medicalBasis && cat.method === CalculationMethod.Fixed);

    if (isFixedBasis) {
      rateDisplay = formatCurrency(grossRate, currencySymbol); // Rand value per person
      exposureDisplay = `${cat.headcount} Lives`;
    } else {
      rateDisplay = formatPct(grossRate); // % of Wageroll
      exposureDisplay = formatWageroll(cat.wageroll, currencySymbol);
    }
  } else if (ben.type === BenefitType.TTD) {
    if (cat.method === CalculationMethod.Stated) {
      rateDisplay = formatPct(grossRate);
      // Exposure is Wageroll (Salary% absorbed into Rate)
      exposureDisplay = formatWageroll(cat.wageroll, currencySymbol);
    } else {
      rateDisplay = formatPct(grossRate);
      const totalWeekly = (ben.weeklyBenefit || 0) * cat.headcount;
      exposureDisplay = `${formatWageroll(totalWeekly, currencySymbol)} (Wkly)`;
    }
  } else {
    // Death / PTD
    if (cat.method === CalculationMethod.Stated) {
      rateDisplay = formatPct(grossRate);
      // Exposure is Wageroll (Multiple absorbed into Rate)
      exposureDisplay = formatWageroll(cat.wageroll, currencySymbol);
    } else {
      rateDisplay = formatPct(grossRate);
      const totalExp = (ben.sumInsured || 0) * cat.headcount;
      exposureDisplay = formatWageroll(totalExp, currencySymbol);
    }
  }

  return { rateDisplay, exposureDisplay };
};

/**
 * Generates the narrative description for a benefit (e.g. "2x Annual Earnings").
 */
export const getBenefitDescription = (
  cat: Category,
  ben: BenefitConfig,
  currencySymbol: string,
) => {
  if (ben.type === BenefitType.Death || ben.type === BenefitType.PTD) {
    if (cat.method === CalculationMethod.Fixed) {
      return `${formatCurrency(ben.sumInsured || 0, currencySymbol)} per person`;
    } else {
      return `${ben.multiple}x Annual Earnings`;
    }
  }
  if (ben.type === BenefitType.TTD) {
    const earnings =
      cat.method === CalculationMethod.Fixed
        ? `${formatCurrency(ben.weeklyBenefit || 0, currencySymbol)} /wk`
        : 'Weekly Earnings';
    return `${ben.salaryPct}% of ${earnings} (${ben.benefitPeriod} wks, ${ben.excessDays}d excess)`;
  }
  if (ben.type === BenefitType.Medical) {
    return `Limit: ${formatCurrency(ben.medicalLimit || 0, currencySymbol)} (Excess: ${formatCurrency(ben.medicalExcess || 0, currencySymbol)})`;
  }
  return 'As per Policy Wording';
};
